import "../App.css";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Warning from "../components/Warning";
import React, { useRef, useEffect } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { LiquidGlass } from "@liquidglass/react";
import CustomImage from "../components/CustomImage";

const Art = () => {
  const frameRef = useRef(null);
  const imgRef = useRef(null);
  const glowRef = useRef(null);

  // Lerp state for smooth tilt — matches Home
  const tilt = useRef({ x: 0, y: 0, tx: 0, ty: 0, running: false });

  const startLerp = () => {
    if (tilt.current.running) return;
    tilt.current.running = true;
    const loop = () => {
      const t = tilt.current;
      t.x += (t.tx - t.x) * 0.1;
      t.y += (t.ty - t.y) * 0.1;
      if (imgRef.current) {
        imgRef.current.style.transform = `rotateX(${t.y}deg) rotateY(${t.x}deg)`;
      }
      const done = Math.abs(t.tx - t.x) < 0.01 && Math.abs(t.ty - t.y) < 0.01;
      if (done) {
        t.x = t.tx; t.y = t.ty;
        if (imgRef.current) imgRef.current.style.transform = `rotateX(${t.y}deg) rotateY(${t.x}deg)`;
        t.running = false;
      } else {
        frameRef.current = requestAnimationFrame(loop);
      }
    };
    frameRef.current = requestAnimationFrame(loop);
  };

  const handleMouseMove = (e) => {
    if (!imgRef.current) return;
    const { left, top, width, height } = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width - 0.5) * -15;
    const y = -((e.clientY - top) / height - 0.5) * -15;
    tilt.current.tx = x;
    tilt.current.ty = y;
    startLerp();
  };

  const handleMouseLeave = () => {
    tilt.current.tx = 0;
    tilt.current.ty = 0;
    startLerp();
  };

  // Global cursor glow tracker — matches Home
  useEffect(() => {
    const glow = glowRef.current;
    if (!glow) return;

    let raf;
    const onMove = (e) => {
      const pt = e.touches?.[0] || e;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        glow.style.setProperty("--mx", `${pt.clientX}px`);
        glow.style.setProperty("--my", `${pt.clientY}px`);
        glow.style.opacity = "1";
      });
    };
    const onLeave = () => { glow.style.opacity = "0"; };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseleave", onLeave);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  function importAll(r) {
    return r.keys().map(r);
  }

  const coverArtImages = importAll(
    require.context(
      "../components/images/Art/coverArt",
      false,
      /\.(png|jpe?g|svg)$/
    )
  ).sort((a, b) => b.localeCompare(a));

  function generateImageGrid(images) {
    return (
      <div className="bigGrid">
        {images.map((image, index) => {
          const imageName = image.split("/").pop().split(".")[0];
          const title = imageName.replace(/^\d+\s*/, "").trim();
          return (
            <div key={index} className="image-container">
              <a href={`/portfolio?art=${imageName}`}>
                <CustomImage
                  src={image}
                  className="coverArt"
                  alt={`Art Cover ${index + 1}`}
                />
                <div className="overlay-text">{`${title}`}</div>
              </a>
            </div>
          );
        })}
      </div>
    );
  }

  function CoverArtGallery() {
    return (
      <div>
        {generateImageGrid(coverArtImages)}
        <br />
      </div>
    );
  }

  return (
    <div className="App" style={{ margin: "-8px" }}>

      {/* ── Entrance & hover styles ── */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(22px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        .art-enter-title {
          animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.05s;
        }
        .art-enter-subtitle {
          animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.18s;
        }
        .art-enter-card {
          animation: fadeUp 0.85s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.35s;
        }
        .art-enter-gallery {
          animation: fadeIn 1s ease both;
          animation-delay: 0.6s;
        }
        .art-enter-footer-text {
          animation: fadeIn 1s ease both;
          animation-delay: 0.75s;
        }
      `}</style>

      {/* ── Cursor glow layer ── */}
      <div
        ref={glowRef}
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          opacity: 0,
          transition: "opacity 0.4s ease",
          background: "radial-gradient(circle 260px at var(--mx, 50%) var(--my, 50%), rgba(180,130,255,0.18) 0%, rgba(100,80,220,0.08) 45%, transparent 75%)",
          mixBlendMode: "screen",
        }}
      />

      <HelmetProvider>
        <Helmet>
          <title>Art Portfolio</title>
          <meta
            name="Most of my art, shoved into a single page."
            content="Street, sport, portrait, and product photography. Oh, and some 3D art."
            description="Street, sport, portrait, and product photography. Oh, and some 3D art."
            author="Jack Frederick"
            image="%PUBLIC_URL%/favcon.ico"
          />
        </Helmet>
      </HelmetProvider>

      <Warning />
      <Navbar />

      <header className="Home">
        <div className="screenBox Minsize">
          <p className="Homethick pullUp art-enter-title">Just a Peek of my Art</p>

          <p className="Homemid art-enter-subtitle">
            Street, sport, portrait, and product photography. Oh, and some 3D art.
          </p>

          <br />

          <div
            className="imageWrapper art-enter-card"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ perspective: "1000px" }}
          >
            <div ref={imgRef} style={{ transformStyle: "preserve-3d" }}>
              {/* Glow halo behind image — matches Home */}
              <div style={{
                position: "absolute",
                inset: "-18px",
                borderRadius: "inherit",
                background: "radial-gradient(ellipse at 50% 50%, rgba(160,110,255,0.35) 0%, transparent 70%)",
                filter: "blur(18px)",
                zIndex: -1,
                pointerEvents: "none",
              }} />
              <img
                src="CoverArt.jpg"
                alt="Cover"
                className="coverImg roundCornerImage glowImage"
                style={{
                  boxShadow: "0 0 32px 8px rgba(160,110,255,0.25), 0 0 80px 20px rgba(120,80,220,0.12)",
                  display: "block",
                }}
              />
            </div>

            <div className="buttonBar">
              {/* Glow halo behind buttons */}
              <div style={{
                position: "absolute",
                inset: "-12px",
                borderRadius: "16px",
                background: "radial-gradient(ellipse at 50% 50%, rgba(130,100,255,0.28) 0%, transparent 70%)",
                filter: "blur(14px)",
                zIndex: -1,
                pointerEvents: "none",
              }} />

              <LiquidGlass className="Glass" blur={2} saturation={1.1} elasticity={0.3}>
                <a className="Homesmall spaceme art-nav-link" href="/portfolio?art=14%20Exibition%20Special">
                  Exhibition Works
                </a>
                <a className="Homesmall spaceme art-nav-link" href="/lookup">
                  Media Lookup Code
                </a>
                <a
                  className="Homesmall spaceme art-nav-link"
                  href="https://www.instagram.com/engineering_jack/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Instagram
                </a>
              </LiquidGlass>
            </div>
          </div>

          <div className="photoSpacer" />

          <div className="art-enter-gallery">
            <div className="bigGrid">{CoverArtGallery()}</div>
          </div>

          <br /><br /><br />

          <p className="Homesmall art-enter-footer-text">
            Some folders are still being migrated, please wait to view other content.
          </p>

          <br /><br /><br />
          <div className="photoSpacer" />
          <div className="photoSpacer" />
        </div>
      </header>

      <Footer />
    </div>
  );
};

export default Art;